import React from "react";
import "./ExtensionsDropdown.scss";

import { useDispatch, useSelector } from "react-redux";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { Button } from "src/app/components/Button/Button";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
} from "src/app/components/Dropdown/Dropdown";
import { AutomationsSection } from "src/app/SettingsModal/tabs/Automations/Automations";

import { useHistory } from "react-router";
import { SettingsContext, SettingsTab } from "src/app/SettingsModal/enums";
import { openSettingsModal } from "src/app/SettingsModal/methods";
import { ExtensionsSection } from "src/app/SettingsModal/tabs/Extensions/Extensions";
import { setEditedAutomationData } from "src/redux/automation/automationActions";
import { AppLocation } from "src/redux/main/mainReducer";
import { RootState } from "src/redux/reducers";
import { WsExtension, ExtensionTypeEnum } from "src/types";
import { ReactComponent as LightningIcon } from "../../../images/lightning.svg";
import AutomationsEnum from "../../SettingsModal/tabs/Automations/AutomationsEnum";
import { ProjectPermissionsEnum } from "../../../utils/PermissionsEnums";

interface Props {
  location: AppLocation;
}

export const ExtensionsDropdown: React.FC<Props> = (props) => {
  const { location } = props;

  const {
    projectReducer: { projectBasicData },
  } = useSelector((state: RootState) => state);

  const history = useHistory();
  const dispatch = useDispatch();

  const enabledAutomations = projectBasicData?.wsAutomations.filter(
    (a) => a.enabled,
  );
  const enabledExtensions = projectBasicData?.wsExtensions.filter((e) => {
    if (e?.type === ExtensionTypeEnum.CreatorContentMonitoring) {
      return e.settings.monitors.some((m) => m.enabled);
    }
    return e?.enabled;
  });

  if (!enabledAutomations?.length && !enabledExtensions?.length) return null;

  const creatorDetectionList = enabledAutomations?.filter(
    (a) =>
      a.wsAutomationName === AutomationsEnum.CreatorDetectionRunnableAutomation,
  );
  const publicationDetectionList = enabledAutomations?.filter(
    (a) =>
      a.wsAutomationName ===
      AutomationsEnum.PublicationDetectionRunnableAutomation,
  );
  const publicationSynchronizationList = enabledAutomations?.filter(
    (a) =>
      a.wsAutomationName ===
      AutomationsEnum.PublicationSynchronizationRunnableAutomation,
  );

  const openAutomations = (
    section: AutomationsSection,
    automationData?: any,
  ) => {
    const manageAutomations = projectBasicData?.permissions?.project?.includes(
      ProjectPermissionsEnum.MANAGE_AUTOMATIONS,
    );

    const automationsSection = manageAutomations
      ? section
      : AutomationsSection.AutomationsMain;

    if (automationData) {
      dispatch(setEditedAutomationData(automationData));
    }

    openSettingsModal(
      history,
      location,
      SettingsContext.Project,
      SettingsTab.Automations,
      automationsSection,
    );
  };

  const openExtensionRecruitmentFormDetails = () => {
    openSettingsModal(
      history,
      location,
      SettingsContext.Project,
      SettingsTab.Extensions,
      ExtensionsSection.RecruitmentForm,
    );
  };

  const openCreatorContentMonitoringList = () => {
    openSettingsModal(
      history,
      location,
      SettingsContext.Project,
      SettingsTab.Extensions,
      ExtensionsSection.CreatorContentMonitoring,
    );
  };

  const openExtensionsList = () => {
    openSettingsModal(
      history,
      location,
      SettingsContext.Project,
      SettingsTab.Extensions,
      ExtensionsSection.ExtensionsList,
    );
  };

  const renderExtensionsListOptions = (
    extensionList: WsExtension[] | undefined,
  ) => {
    if (!extensionList?.length) return null;

    return (
      <>
        <div className="extensions-dropdown__subheader">
          <IDHFormattedMessage id="ws_extensions" defaultMessage="Extensions" />
        </div>
        {extensionList.map((extensionData: WsExtension) => {
          switch (extensionData.type) {
            case ExtensionTypeEnum.RecruitmentForm:
              return (
                <DropdownMenuItem
                  onClick={openExtensionRecruitmentFormDetails}
                  key={extensionData.uuid}
                  data-qa="recruitment-form-button"
                >
                  <IDHFormattedMessage
                    id="ws_recruitment_form"
                    defaultMessage="Recruitment form"
                  />
                </DropdownMenuItem>
              );
            case ExtensionTypeEnum.WsProjectLock:
              return (
                <DropdownMenuItem
                  onClick={openExtensionsList}
                  key={extensionData.uuid}
                  data-qa="lock-project-editing-button"
                >
                  <IDHFormattedMessage
                    id="ws_lock_project_editing_extension"
                    defaultMessage="Lock project editing"
                  />
                </DropdownMenuItem>
              );
            case ExtensionTypeEnum.CreateSingleAndSplitPayments:
              return (
                <DropdownMenuItem
                  key={extensionData.uuid}
                  style={{ cursor: "default" }}
                  data-qa="create-single-and-split-payments-button"
                >
                  <IDHFormattedMessage
                    id="ws_create_single_and_split_payments"
                    defaultMessage="Create single and split payments"
                  />
                </DropdownMenuItem>
              );
            case ExtensionTypeEnum.CreatorContentMonitoring:
              return (
                <DropdownMenuItem
                  onClick={openCreatorContentMonitoringList}
                  key={extensionData.uuid}
                  data-qa="creator-content-monitoring-button"
                >
                  <IDHFormattedMessage
                    id="ws_creator_content_monitoring"
                    defaultMessage="Creator content monitoring"
                  />
                </DropdownMenuItem>
              );
            case ExtensionTypeEnum.StorePublishedContent:
              return (
                <DropdownMenuItem
                  onClick={openExtensionsList}
                  key={extensionData.uuid}
                  data-qa="creator-content-monitoring-button"
                >
                  <IDHFormattedMessage
                    id="ws_published_content_auto_collection"
                    defaultMessage="Published Content Auto-Collection"
                  />
                </DropdownMenuItem>
              );
            default:
              return null;
          }
        })}
      </>
    );
  };

  const renderAutomationsOptions = () => {
    if (!enabledAutomations?.length) return;

    return (
      <>
        <div className="extensions-dropdown__subheader">
          <IDHFormattedMessage
            id="ws_automations"
            defaultMessage="Automations"
          />
        </div>

        {(creatorDetectionList?.length ?? 0) > 0 && (
          <DropdownMenuItem
            onClick={() => openAutomations(AutomationsSection.AutomationsMain)}
            data-qa="creators-detection-button"
          >
            <IDHFormattedMessage
              id="ws_creators_detection"
              defaultMessage="Creators detection"
            />
          </DropdownMenuItem>
        )}

        {(publicationDetectionList?.length ?? 0) > 0 && (
          <DropdownMenuItem
            onClick={() =>
              openAutomations(AutomationsSection.PublicationsDetection)
            }
            data-qa="publication-detection-button"
          >
            <IDHFormattedMessage
              id="ws_publication_detection"
              defaultMessage="Publication detection"
            />
          </DropdownMenuItem>
        )}

        {(publicationSynchronizationList?.length ?? 0) > 0 && (
          <DropdownMenuItem
            onClick={() =>
              openAutomations(AutomationsSection.PublicationsSynchronization)
            }
            data-qa="publications-synchronization-button"
          >
            <IDHFormattedMessage
              id="ws_publication_synchronization"
              defaultMessage="Publications synchronization"
            />
          </DropdownMenuItem>
        )}
      </>
    );
  };

  if (
    (!enabledAutomations?.length && !enabledExtensions?.length) ||
    !projectBasicData?.permissions?.project?.includes(
      ProjectPermissionsEnum.DISPLAY_PROJECT_CONFIGURATION,
    )
  )
    return null;

  return (
    <Dropdown className="extensions-dropdown" toRight>
      {(isOpen, setIsOpen) => (
        <>
          <Button
            variant="white"
            size="thin"
            textWithIcon
            onClick={() => setIsOpen((prev) => !prev)}
            dataQa="dropdown-extensions-button"
          >
            <LightningIcon />
            {(enabledAutomations?.length ?? 0) +
              (enabledExtensions?.length ?? 0)}
          </Button>

          <DropdownMenu isOpen={isOpen}>
            {renderAutomationsOptions()}
            {renderExtensionsListOptions(enabledExtensions)}
          </DropdownMenu>
        </>
      )}
    </Dropdown>
  );
};
